import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toBerlinDateKey } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import { Banner, Skeleton } from '../../../ds/components';
import { errorMessage } from '../../../lib/api';
import { addDays, addMonths, dayNumber, monthGrid, startOfWeek } from '../../calendar/dates';
import {
  bookingDaysKey,
  bookingTimesKey,
  useBookingDays,
  useBookingTimes,
  type BookingTime,
  type BookingTimes,
  type SlotTaken,
} from './api';
import { monthLabel, monthName, monthOf, withoutWeekday } from './format';
import styles from './Booking.module.css';

const WEEKDAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'] as const;

export interface NoSlotsAction {
  /** „Oder Jonas Weiler ruft Sie an, sobald etwas frei wird." */
  hostName: string;
  onCallback: () => void;
}

export interface SlotChooserProps {
  orgSlug: string;
  typeSlug: string;
  earliest: string;
  latest: string;
  mobile: boolean;
  /** Day to open first (the held or previous time); else the first free day of the booking window. */
  initialDate?: string | null | undefined;
  /** Time to show as chosen when the chooser opens (the held time after „‹ Andere Zeit wählen"). */
  initialTime?: string | null | undefined;
  /** A `409 SLOT_TAKEN`: the chooser jumps to its day and shows the lost time as taken. */
  race?: SlotTaken | null | undefined;
  /** Banner above the calendar (race, 2-hour rule, errors). */
  banner?: ReactNode | undefined;
  footnote: string;
  busy: boolean;
  /** Mobile sticky CTA („Weiter mit 16:00", „Auf 17:30 umbuchen"). */
  mobileLabel: (time: BookingTime) => string;
  /** Mobile note under the CTA („Der alte Termin wird dann freigegeben."). */
  mobileNote?: string | undefined;
  /** Mobile heading above the strip („Neuen Termin wählen", Buchungsseite 10). */
  mobileHeading?: string | undefined;
  /** Screen 04's callback part (booking page only). */
  noSlots?: NoSlotsAction | null | undefined;
  onContinue: (time: BookingTime) => void;
}

/** Days of `month` with availability, or null while loading. */
function useAvailability(orgSlug: string, typeSlug: string, months: string[]) {
  const first = useBookingDays(orgSlug, typeSlug, months[0] ?? null);
  const second = useBookingDays(orgSlug, typeSlug, months[1] ?? null);
  return useMemo(() => {
    const map = new Map<string, boolean>();
    for (const response of [first.data, months[1] ? second.data : undefined]) {
      for (const day of response?.days ?? []) map.set(day.date, day.available);
    }
    return {
      map,
      loading: first.isPending || (months[1] !== undefined && second.isPending),
      error: first.error ?? (months[1] ? second.error : null),
      nextAvailable: first.data?.nextAvailable ?? null,
      monthDays: first.data?.days ?? null,
    };
  }, [first.data, first.isPending, first.error, second.data, second.isPending, second.error, months]);
}

/**
 * Buchungsseite 01/03/04/06 — month calendar (desktop) or five-day strip (390), the day's times, the
 * „Weiter" beside the chosen time (desktop) or the sticky CTA (390), and screen 04 when a month is full.
 */
export function SlotChooser(props: SlotChooserProps) {
  const {
    orgSlug,
    typeSlug,
    earliest,
    latest,
    mobile,
    initialDate,
    initialTime,
    race,
    banner,
    footnote,
    busy,
    mobileLabel,
    mobileNote,
    mobileHeading,
    noSlots,
    onContinue,
  } = props;
  const queryClient = useQueryClient();
  const [month, setMonth] = useState(() => monthOf(initialDate ?? earliest));
  const [date, setDate] = useState<string | null>(initialDate ?? null);
  const [time, setTime] = useState<string | null>(initialTime ?? null);
  const [monthView, setMonthView] = useState(false);

  // The mobile strip shows Mo–Fr of the chosen (or first) day's week; it may reach into the next month.
  const stripMonday = startOfWeek(date ?? earliest);
  const stripMonths = useMemo(() => {
    const months = [month];
    if (mobile && !monthView) {
      const other = [monthOf(stripMonday), monthOf(addDays(stripMonday, 4))].filter(
        (entry) => entry !== month,
      );
      if (other[0]) months.push(other[0]);
    }
    return months;
  }, [month, mobile, monthView, stripMonday]);
  const availability = useAvailability(orgSlug, typeSlug, stripMonths);
  const times = useBookingTimes(orgSlug, typeSlug, date);

  // A race: jump to the lost time's day and show the server's list (the lost time taken).
  useEffect(() => {
    if (!race) return;
    const day = race.startsAt ? toBerlinDateKey(race.startsAt) : date;
    if (day && race.times) {
      queryClient.setQueryData<BookingTimes>(bookingTimesKey(orgSlug, typeSlug, day), {
        dateLabel: race.dateLabel ?? '',
        times: race.times,
      });
    }
    void queryClient.invalidateQueries({
      queryKey: bookingDaysKey(orgSlug, typeSlug, monthOf(day ?? earliest)),
    });
    if (day) {
      setDate(day);
      setMonth(monthOf(day));
    }
    setTime(null);
    // Only a new race moves the chooser.
  }, [race]);

  // First free day of the shown month once its days are known (the frames open with a day chosen); a day
  // the customer chose in this month (or in the mobile strip) stays.
  useEffect(() => {
    if (availability.monthDays === null) return;
    if (date && monthOf(date) === month) return;
    if (date && mobile && !monthView && availability.map.has(date)) return;
    setDate(availability.monthDays.find((day) => day.available)?.date ?? null);
    setTime(null);
  }, [availability.monthDays, month]);

  const monthHasFreeDay = availability.monthDays?.some((day) => day.available) ?? true;
  const firstMonth = monthOf(earliest);
  const lastMonth = monthOf(latest);
  const selectedTime = times.data?.times.find((entry) => entry.startsAt === time && !entry.taken) ?? null;

  function pickDay(day: string) {
    setDate(day);
    setTime(null);
    if (monthOf(day) !== month) setMonth(monthOf(day));
    setMonthView(false);
  }

  function jumpTo(day: string) {
    setMonth(monthOf(day));
    setDate(day);
    setTime(null);
    setMonthView(false);
  }

  const noSlotsPanel = (
    <div className={styles.noSlots}>
      <div className={mobile ? styles.noSlotsTitleMobile : styles.noSlotsTitle}>
        Im {monthName(month)} ist nichts mehr frei.
      </div>
      {availability.nextAvailable ? (
        <div className={styles.noSlotsText}>
          Der nächste freie Termin ist am {availability.nextAvailable.label}.
        </div>
      ) : null}
      {!mobile && availability.nextAvailable ? (
        <button
          type="button"
          className={styles.primaryButton}
          onClick={() => jumpTo(availability.nextAvailable?.date ?? earliest)}
        >
          Zum {withoutWeekday(availability.nextAvailable.label)}
        </button>
      ) : null}
      {!mobile && noSlots ? (
        <>
          <div className={styles.noSlotsCallback}>
            Oder {noSlots.hostName} ruft Sie an, sobald etwas frei wird.
          </div>
          <button type="button" className={styles.secondaryButton} onClick={noSlots.onCallback}>
            Rückruf anfordern
          </button>
        </>
      ) : null}
    </div>
  );

  const timeList = (
    <div className={clsx(styles.times, mobile && styles.timesMobile)}>
      {!mobile ? (
        <div className={styles.timesDay}>{times.data?.dateLabel ?? ' '}</div>
      ) : times.data ? (
        <div className={styles.timesDayMobile}>{times.data.dateLabel}</div>
      ) : null}
      <div className={mobile ? styles.timeColumn : styles.timeScroll}>
        {times.isPending && date !== null ? (
          <>
            <Skeleton height={44} />
            <Skeleton height={44} />
            <Skeleton height={44} />
          </>
        ) : times.isError ? (
          <Banner tone="danger">{errorMessage(times.error)}</Banner>
        ) : (
          (times.data?.times ?? []).map((entry) => {
            if (entry.taken) {
              return (
                <span
                  key={entry.startsAt}
                  className={clsx(styles.time, styles.timeTaken)}
                  aria-label={`${entry.label} · vergeben`}
                >
                  {entry.label}
                </span>
              );
            }
            const chosen = entry.startsAt === time;
            if (chosen && !mobile) {
              return (
                <div key={entry.startsAt} className={styles.timeChosenRow}>
                  <span className={clsx(styles.time, styles.timeChosen)} aria-current="true">
                    {entry.label}
                  </span>
                  <button
                    type="button"
                    className={styles.primaryButton}
                    disabled={busy}
                    aria-busy={busy || undefined}
                    onClick={() => onContinue(entry)}
                  >
                    Weiter
                  </button>
                </div>
              );
            }
            return (
              <button
                key={entry.startsAt}
                type="button"
                className={clsx(styles.time, chosen ? styles.timeChosen : styles.timeFree)}
                aria-pressed={chosen}
                onClick={() => setTime(entry.startsAt)}
              >
                {entry.label}
              </button>
            );
          })
        )}
      </div>
    </div>
  );

  const monthGridView = (
    <div className={styles.month}>
      <div className={styles.monthHead}>
        <span className={styles.monthTitle}>{monthLabel(month)}</span>
        <span className={styles.monthNav}>
          <button
            type="button"
            className={styles.navButton}
            aria-label="Vorheriger Monat"
            disabled={month <= firstMonth}
            onClick={() => setMonth(monthOf(addMonths(`${month}-01`, -1)))}
          >
            ‹
          </button>
          <button
            type="button"
            className={styles.navButton}
            aria-label="Nächster Monat"
            disabled={month >= lastMonth}
            onClick={() => setMonth(monthOf(addMonths(`${month}-01`, 1)))}
          >
            ›
          </button>
        </span>
      </div>
      <div
        className={styles.monthGrid}
        role="group"
        aria-label={monthLabel(month)}
        aria-busy={availability.loading || undefined}
      >
        {WEEKDAYS.map((label) => (
          <span key={label} className={styles.weekday}>
            {label}
          </span>
        ))}
        {monthGrid(`${month}-01`)
          .flat()
          .map((cell, index) => {
            if (!cell.inMonth) {
              // Leading days of the previous month stay empty; the grid ends with the month.
              return cell.key < `${month}-01` ? <span key={`pad-${index}`} /> : null;
            }
            const available = availability.map.get(cell.key) === true;
            const selected = cell.key === date;
            const label = `${dayNumber(cell.key)}. ${monthName(month)}`;
            if (!available) {
              return (
                <span
                  key={cell.key}
                  className={clsx(styles.day, styles.dayOff, selected && styles.daySelected)}
                  aria-label={`${label} · nicht verfügbar`}
                >
                  {dayNumber(cell.key)}
                </span>
              );
            }
            return (
              <button
                key={cell.key}
                type="button"
                className={clsx(styles.day, selected ? styles.daySelected : styles.dayFree)}
                aria-pressed={selected}
                aria-label={`${label} · freie Zeiten`}
                onClick={() => pickDay(cell.key)}
              >
                {dayNumber(cell.key)}
              </button>
            );
          })}
      </div>
    </div>
  );

  const strip = (
    <div
      className={styles.strip}
      role="group"
      aria-label={monthName(monthOf(stripMonday))}
      aria-busy={availability.loading || undefined}
    >
      {Array.from({ length: 5 }, (_, index) => addDays(stripMonday, index)).map((day, index) => {
        const available = availability.map.get(day) === true;
        const selected = day === date;
        const content = (
          <>
            <span className={styles.stripWeekday}>{WEEKDAYS[index]}</span>
            <span className={styles.stripNumber}>{dayNumber(day)}</span>
          </>
        );
        const label = `${dayNumber(day)}. ${monthName(monthOf(day))}`;
        return available ? (
          <button
            key={day}
            type="button"
            className={clsx(styles.stripDay, selected ? styles.stripSelected : styles.stripFree)}
            aria-pressed={selected}
            aria-label={`${label} · freie Zeiten`}
            onClick={() => pickDay(day)}
          >
            {content}
          </button>
        ) : (
          <span
            key={day}
            className={clsx(styles.stripDay, styles.stripOff)}
            aria-label={`${label} · nicht verfügbar`}
          >
            {content}
          </span>
        );
      })}
    </div>
  );

  if (availability.error) {
    return <Banner tone="danger">{errorMessage(availability.error)}</Banner>;
  }

  if (mobile) {
    const showNoSlots = !monthHasFreeDay && !availability.loading;
    return (
      <div className={styles.mobileChooser}>
        {banner}
        {monthView ? (
          monthGridView
        ) : (
          <>
            <div className={styles.stripHead}>
              <span className={styles.stripMonth}>
                {mobileHeading ?? monthName(monthOf(date ?? stripMonday))}
              </span>
              <button type="button" className={styles.textLink} onClick={() => setMonthView(true)}>
                Monat ansehen
              </button>
            </div>
            {strip}
          </>
        )}
        {showNoSlots ? noSlotsPanel : timeList}
        <div className={styles.sticky}>
          {showNoSlots ? (
            <>
              {availability.nextAvailable ? (
                <button
                  type="button"
                  className={styles.stickyPrimary}
                  onClick={() => jumpTo(availability.nextAvailable?.date ?? earliest)}
                >
                  Zum {withoutWeekday(availability.nextAvailable.label)}
                </button>
              ) : null}
              {noSlots ? (
                <button type="button" className={styles.stickySecondary} onClick={noSlots.onCallback}>
                  Rückruf anfordern
                </button>
              ) : null}
            </>
          ) : (
            <>
              <button
                type="button"
                className={styles.stickyPrimary}
                disabled={selectedTime === null || busy}
                aria-busy={busy || undefined}
                onClick={() => {
                  if (selectedTime) onContinue(selectedTime);
                }}
              >
                {selectedTime ? mobileLabel(selectedTime) : 'Weiter'}
              </button>
              {mobileNote ? <span className={styles.stickyNote}>{mobileNote}</span> : null}
            </>
          )}
        </div>
      </div>
    );
  }

  const showNoSlots = !monthHasFreeDay && !availability.loading;
  return (
    <div className={styles.chooser}>
      {banner}
      <div className={clsx(styles.slotGrid, showNoSlots && styles.slotGridWide)}>
        {monthGridView}
        {showNoSlots ? noSlotsPanel : timeList}
      </div>
      {showNoSlots ? null : <div className={styles.footnote}>{footnote}</div>}
    </div>
  );
}
